import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Message, Vide } from "@cabinet/charte";
import {
  dossiersApi,
  lettrageApi,
  ErreurApi,
  type CleDEcriture,
  type EcritureLettrableVue,
  type LettrageVue,
} from "../api/client";
import { formaterDate, formaterMontant, formaterNombre } from "../domaine/formats";
import {
  basculer,
  cleDe,
  LIBELLE_DE_LA_NATURE,
  natureDeLEcart,
  selectionDe,
  statutPrevu,
  totauxDeSelection,
} from "../domaine/lettrage";
import { useSession } from "../session";

/**
 * Lettrage des comptes de tiers (EF-0407).
 *
 * L'écran se tient sur un compte auxiliaire : un compte lettrable et, s'il
 * est collectif, un tiers. Il montre ses écritures libres, où l'on coche ce
 * que l'on rapproche, et ses lettrages en cours, qu'on complète, qu'on solde
 * par un écart de règlement ou qu'on annule. L'automate propose avant
 * d'agir : ses appariements se relisent, puis s'appliquent d'un coup.
 */
export function PageLettrage() {
  const { id = "" } = useParams();
  const [parametres, setParametres] = useSearchParams();
  const compte = parametres.get("compte")?.trim() ?? "";
  const tiers = parametres.get("tiers")?.trim() || null;

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const comptes = useQuery({ queryKey: ["lettrage-comptes", id], queryFn: () => lettrageApi.comptes(id) });

  function ouvrir(nouveauCompte: string, nouveauTiers: string) {
    const suivants = new URLSearchParams();
    if (nouveauCompte.trim() !== "") suivants.set("compte", nouveauCompte.trim());
    if (nouveauTiers.trim() !== "") suivants.set("tiers", nouveauTiers.trim());
    setParametres(suivants, { replace: true });
  }

  const choixCourant = `${compte}|${tiers ?? ""}`;
  const choixConnu = comptes.data?.some((c) => `${c.compte}|${c.tiers ?? ""}` === choixCourant) ?? false;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Lettrage"
        description="Une facture et son règlement se rapprochent sous une même lettre. Le lettrage est total quand les écritures se soldent, partiel sinon : il porte alors son solde ouvert, jusqu'à un complément ou un écart de règlement. Une lettre attribuée ne se réattribue jamais."
      />

      <form
        className="filtres-plan filtres-lettrage"
        onSubmit={(e) => {
          e.preventDefault();
          const champs = new FormData(e.currentTarget);
          ouvrir(String(champs.get("compte") ?? ""), String(champs.get("tiers") ?? ""));
        }}
      >
        <select
          value={choixConnu ? choixCourant : ""}
          onChange={(e) => {
            const [c, t] = e.target.value.split("|");
            ouvrir(c ?? "", t ?? "");
          }}
          aria-label="Compte auxiliaire"
        >
          <option value="">— Comptes auxiliaires à lettrer —</option>
          {comptes.data?.map((c) => (
            <option key={`${c.compte}|${c.tiers ?? ""}`} value={`${c.compte}|${c.tiers ?? ""}`}>
              {c.compte}
              {c.tiers != null ? ` · ${c.tiers}` : ""} — {formaterNombre(c.nombreDEcritures)} écritures, solde non
              lettré {formaterMontant(Math.abs(c.soldeNonLettre))} {c.soldeNonLettre < 0 ? "C" : c.soldeNonLettre > 0 ? "D" : ""}
            </option>
          ))}
        </select>
        <input key={`compte-${compte}`} name="compte" defaultValue={compte} placeholder="Compte — 4411" aria-label="Compte" />
        <input key={`tiers-${tiers ?? ""}`} name="tiers" defaultValue={tiers ?? ""} placeholder="Tiers — ATLAS" aria-label="Tiers" />
        <button type="submit" className="secondaire">
          Ouvrir
        </button>
      </form>

      {dossier.error != null && (
        <Message type="erreur" titre={dossier.error instanceof ErreurApi ? dossier.error.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{dossier.error.message}</p>
        </Message>
      )}
      {comptes.error != null && (
        <Message type="erreur" titre={comptes.error instanceof ErreurApi ? comptes.error.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{comptes.error.message}</p>
        </Message>
      )}

      {compte === "" ? (
        comptes.data != null && comptes.data.length === 0 ? (
          <Vide>Aucune écriture validée n'attend de lettrage sur ce dossier.</Vide>
        ) : (
          <Vide>Choisissez un compte auxiliaire pour voir ses écritures à rapprocher.</Vide>
        )
      ) : (
        // La clé remet la sélection à zéro quand on change de compte : une
        // écriture cochée n'a de sens que sur le compte où elle l'a été.
        <SituationDuCompte key={choixCourant} dossier={id} compte={compte} tiers={tiers} />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function SituationDuCompte({ dossier, compte, tiers }: { dossier: string; compte: string; tiers: string | null }) {
  const { peut } = useSession();
  const cache = useQueryClient();
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [propositions, setPropositions] = useState<EcritureLettrableVue[][] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [nouvelle, setNouvelle] = useState<string | null>(null);

  const situation = useQuery({
    queryKey: ["lettrage", dossier, compte, tiers],
    queryFn: () => lettrageApi.situation(dossier, compte, tiers),
  });

  const peutLettrer = peut("SAISIR_ECRITURE");
  const peutConstater = peut("VALIDER_ECRITURE");

  const rafraichir = () => {
    setSelection(new Set());
    setPropositions(null);
    setErreur(null);
    void cache.invalidateQueries({ queryKey: ["lettrage"] });
    void cache.invalidateQueries({ queryKey: ["lettrage-comptes", dossier] });
    void cache.invalidateQueries({ queryKey: ["grand-livre"] });
    void cache.invalidateQueries({ queryKey: ["balance"] });
    void cache.invalidateQueries({ queryKey: ["pieces"] });
    void cache.invalidateQueries({ queryKey: ["chaine", dossier] });
  };
  const signaler = (echec: unknown) => {
    setNouvelle(null);
    setErreur(echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti.");
  };
  const annoncer = (lettrage: LettrageVue, quoi: string) => {
    rafraichir();
    setNouvelle(`${quoi} — lettre ${lettrage.lettre}, ${lettrage.statutLibelle.toLowerCase()}.`);
  };

  const lettrer = useMutation({
    mutationFn: (lignes: CleDEcriture[]) => lettrageApi.lettrer(dossier, compte, tiers, lignes),
    onSuccess: (cree) => annoncer(cree, "Lettrage enregistré"),
    onError: signaler,
  });
  const completer = useMutation({
    mutationFn: ({ id, lignes }: { id: string; lignes: CleDEcriture[] }) => lettrageApi.completer(id, lignes),
    onSuccess: (complete) => annoncer(complete, "Lettrage complété"),
    onError: signaler,
  });
  const constater = useMutation({
    mutationFn: ({ id, date, libelle }: { id: string; date: string; libelle: string | null }) =>
      lettrageApi.constaterLEcart(id, date, libelle),
    onSuccess: (solde) => annoncer(solde, "Écart de règlement constaté par une pièce d'opérations diverses validée"),
    onError: signaler,
  });
  const annuler = useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => lettrageApi.annuler(id, motif),
    onSuccess: (annule) => annoncer(annule, "Lettrage annulé, écritures rouvertes"),
    onError: signaler,
  });
  const automate = useMutation({
    mutationFn: (appliquer: boolean) => lettrageApi.automatique(dossier, compte, tiers, appliquer),
    onSuccess: (resultat, appliquer) => {
      if (appliquer) {
        rafraichir();
        setNouvelle(
          resultat.lettrages.length === 0
            ? "L'automate n'a rien apparié."
            : `${formaterNombre(resultat.lettrages.length)} lettrage(s) enregistré(s) : ${resultat.lettrages.map((l) => l.lettre).join(", ")}.`,
        );
      } else {
        setErreur(null);
        setNouvelle(null);
        setPropositions(resultat.propositions);
      }
    },
    onError: signaler,
  });

  const nonLettrees = situation.data?.nonLettrees ?? [];
  const totaux = totauxDeSelection(nonLettrees, selection);
  const prevu = statutPrevu(totaux);
  const lignesChoisies: CleDEcriture[] = nonLettrees
    .filter((e) => selection.has(cleDe(e)))
    .map((e) => ({ piece: e.piece, rang: e.rang }));
  const enCours =
    lettrer.isPending || completer.isPending || constater.isPending || annuler.isPending || automate.isPending;

  function demanderLEcart(lettrage: LettrageVue) {
    setErreur(null);
    const nature = natureDeLEcart(lettrage.solde);
    if (nature == null) return;
    const date = window.prompt(
      `Date de la pièce d'écart (AAAA-MM-JJ) — ${LIBELLE_DE_LA_NATURE[nature]} de ${formaterMontant(Math.abs(lettrage.solde))} :`,
      new Date().toISOString().slice(0, 10),
    );
    if (date == null || date.trim() === "") return;
    const libelle = window.prompt("Libellé (facultatif) :", "");
    constater.mutate({ id: lettrage.id, date: date.trim(), libelle: libelle?.trim() || null });
  }

  function demanderLAnnulation(lettrage: LettrageVue) {
    setErreur(null);
    const motif = window.prompt(`Motif de l'annulation du lettrage ${lettrage.lettre} (obligatoire, consigné) :`, "");
    if (motif == null) return;
    annuler.mutate({ id: lettrage.id, motif: motif.trim() });
  }

  if (situation.isPending) return <Chargement quoi="le compte auxiliaire" />;

  if (situation.error != null) {
    return (
      <Message type="erreur" titre={situation.error instanceof ErreurApi ? situation.error.titre : "Erreur"}>
        <p style={{ margin: "6px 0 0" }}>{situation.error.message}</p>
      </Message>
    );
  }

  const donnees = situation.data;

  return (
    <>
      {erreur != null && (
        <Message type="erreur" titre="Lettrage refusé">
          <p style={{ margin: "6px 0 0" }}>{erreur}</p>
        </Message>
      )}
      {nouvelle != null && <Message type="succes">{nouvelle}</Message>}

      <div className="carte">
        <h2>
          <span className="numero-compte">{donnees.compte}</span>
          {donnees.tiers != null && <> · {donnees.tiers}</>}{" "}
          <span className="discret-texte">
            — écritures non lettrées, solde{" "}
            {formaterMontant(Math.abs(donnees.soldeNonLettre))}{" "}
            {donnees.soldeNonLettre < 0 ? "C" : donnees.soldeNonLettre > 0 ? "D" : ""}
          </span>
        </h2>

        {nonLettrees.length === 0 ? (
          <Vide>Aucune écriture validée n'attend de lettrage sur ce compte.</Vide>
        ) : (
          <div className="tableau-conteneur">
            <table className="lettrage-selection">
              <thead>
                <tr>
                  {peutLettrer && <th className="case" aria-label="Sélection" />}
                  <th>Date</th>
                  <th>Journal</th>
                  <th className="nombre">N°</th>
                  <th>Libellé</th>
                  <th>Référence</th>
                  <th className="nombre">Débit</th>
                  <th className="nombre">Crédit</th>
                </tr>
              </thead>
              <tbody>
                {nonLettrees.map((ecriture) => {
                  const cle = cleDe(ecriture);
                  const choisie = selection.has(cle);
                  return (
                    <tr
                      key={cle}
                      className={choisie ? "selectionnee" : undefined}
                      onClick={peutLettrer ? () => setSelection((s) => basculer(s, cle)) : undefined}
                    >
                      {peutLettrer && (
                        <td className="case">
                          <input
                            type="checkbox"
                            checked={choisie}
                            onChange={() => setSelection((s) => basculer(s, cle))}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Sélectionner ${ecriture.libelle}`}
                          />
                        </td>
                      )}
                      <td>{formaterDate(ecriture.date)}</td>
                      <td>{ecriture.journal}</td>
                      <td className="nombre">{ecriture.numero ?? "—"}</td>
                      <td>{ecriture.libelle}</td>
                      <td className="discret-texte">{ecriture.reference ?? "—"}</td>
                      <td className="nombre">{ecriture.debit > 0 ? formaterMontant(ecriture.debit) : ""}</td>
                      <td className="nombre">{ecriture.credit > 0 ? formaterMontant(ecriture.credit) : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
              {peutLettrer && (
                <tfoot>
                  <tr className="total">
                    <td colSpan={6}>
                      {prevu === "VIDE" && "Aucune écriture choisie"}
                      {prevu === "TOTAL" && `Lettrage total : ${formaterNombre(totaux.nombre)} écritures qui se soldent`}
                      {prevu === "PARTIEL" && (
                        <>
                          Lettrage partiel : {formaterNombre(totaux.nombre)} écritures, solde ouvert{" "}
                          <span className="ecart">
                            {formaterMontant(Math.abs(totaux.solde))} {totaux.solde < 0 ? "C" : "D"}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="nombre">{formaterMontant(totaux.debit)}</td>
                    <td className="nombre">{formaterMontant(totaux.credit)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {peutLettrer && nonLettrees.length > 0 && (
          <div className="barre-lettrage">
            <button
              type="button"
              disabled={enCours || totaux.nombre < 2}
              onClick={() => {
                setErreur(null);
                lettrer.mutate(lignesChoisies);
              }}
            >
              Lettrer la sélection
              {totaux.nombre > 0 ? ` (${formaterNombre(totaux.nombre)})` : ""}
            </button>
            <button
              type="button"
              className="secondaire"
              disabled={enCours || selection.size === 0}
              onClick={() => setSelection(new Set())}
            >
              Tout décocher
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" className="secondaire" disabled={enCours} onClick={() => automate.mutate(false)}>
              Proposer un lettrage automatique
            </button>
            {propositions != null && propositions.length > 0 && (
              <button type="button" disabled={enCours} onClick={() => automate.mutate(true)}>
                Appliquer les {formaterNombre(propositions.length)} propositions
              </button>
            )}
          </div>
        )}

        {propositions != null && (
          <div className="propositions-de-lettrage">
            {propositions.length === 0 ? (
              <Vide>L'automate ne trouve rien à apparier : ni référence commune, ni montants égaux.</Vide>
            ) : (
              propositions.map((groupe, indice) => (
                <div className="groupe-propose" key={groupe.map(cleDe).join("+")}>
                  <div className="barre-lettrage">
                    <strong>Proposition {indice + 1}</strong>
                    <span className="discret-texte">
                      {groupe.map((e) => `${formaterDate(e.date)} ${e.libelle}${e.reference != null ? ` (${e.reference})` : ""}`).join(" · ")}
                    </span>
                    <span style={{ flex: 1 }} />
                    <button
                      type="button"
                      className="secondaire"
                      disabled={enCours}
                      onClick={() => setSelection(selectionDe(groupe))}
                    >
                      Reprendre dans la sélection
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <h2 style={{ marginTop: 24 }}>Lettrages en cours</h2>
      {donnees.lettrages.length === 0 ? (
        <Vide>Aucun lettrage actif sur ce compte.</Vide>
      ) : (
        donnees.lettrages.map((lettrage) => {
          const nature = natureDeLEcart(lettrage.solde);
          return (
            <div className="carte" key={lettrage.id}>
              <div className="barre-lettrage">
                <span className="lettre">{lettrage.lettre}</span>
                <Etiquette variante={lettrage.statut === "TOTAL" ? "succes" : "avertissement"}>
                  {lettrage.statutLibelle}
                </Etiquette>
                <span className="discret-texte">
                  {formaterNombre(lettrage.ecritures.length)} écritures · débit {formaterMontant(lettrage.totalDebit)} ·
                  crédit {formaterMontant(lettrage.totalCredit)}
                  {lettrage.statut === "PARTIEL" && (
                    <>
                      {" "}
                      · solde ouvert{" "}
                      <span className="ecart">
                        {formaterMontant(Math.abs(lettrage.solde))} {lettrage.solde < 0 ? "C" : "D"}
                      </span>
                    </>
                  )}
                  {" · "}le {formaterDate(lettrage.creeLe)}
                </span>
                <span style={{ flex: 1 }} />
                {peutLettrer && lettrage.statut === "PARTIEL" && (
                  <button
                    type="button"
                    className="secondaire"
                    disabled={enCours || lignesChoisies.length === 0}
                    onClick={() => {
                      setErreur(null);
                      completer.mutate({ id: lettrage.id, lignes: lignesChoisies });
                    }}
                  >
                    Compléter avec la sélection
                  </button>
                )}
                {peutConstater && lettrage.statut === "PARTIEL" && nature != null && (
                  <button type="button" className="secondaire" disabled={enCours} onClick={() => demanderLEcart(lettrage)}>
                    Constater l'écart ({LIBELLE_DE_LA_NATURE[nature]} de {formaterMontant(Math.abs(lettrage.solde))})
                  </button>
                )}
                {peutLettrer && (
                  <button type="button" className="secondaire" disabled={enCours} onClick={() => demanderLAnnulation(lettrage)}>
                    Annuler…
                  </button>
                )}
              </div>
              <div className="tableau-conteneur">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Journal</th>
                      <th className="nombre">N°</th>
                      <th>Libellé</th>
                      <th>Référence</th>
                      <th className="nombre">Débit</th>
                      <th className="nombre">Crédit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lettrage.ecritures.map((ecriture) => (
                      <tr key={cleDe(ecriture)}>
                        <td>{formaterDate(ecriture.date)}</td>
                        <td>{ecriture.journal}</td>
                        <td className="nombre">{ecriture.numero ?? "—"}</td>
                        <td>{ecriture.libelle}</td>
                        <td className="discret-texte">{ecriture.reference ?? "—"}</td>
                        <td className="nombre">{ecriture.debit > 0 ? formaterMontant(ecriture.debit) : ""}</td>
                        <td className="nombre">{ecriture.credit > 0 ? formaterMontant(ecriture.credit) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </>
  );
}
